import React, { useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, List, ListItemButton,
  ListItemText, MenuItem, Paper, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { EmptyState, NeedsHousehold, SectionTitle, useHousehold, useLoad } from '../common';
import {
  MEAL_COURSES, MEAL_KINDS, dayLabel, guestsText, mealCourseText, mealTaskText, mealText, mealWarning, number, parseNumber, searchKey,
  servings, toTimeOnly, toggleIn,
} from '../labels';
import { MemberChips } from '../editing';
import {
  SideChooserDialog, asSide, decimalText, frozenPickerText, leftoverSourceText, planUrl, recipeSubtitle, useWeekParam,
} from './shared';
import { DishDialog } from './DishDialog';

const MAX_RECIPE_RESULTS = 8;
const MAX_DISHES = 8;
const MAX_GUESTS = 50;
/** Select value for "Ainda não sei" (leftovers without a source yet). */
const NO_SOURCE = 'none';

interface Loaded {
  slot: T.MealSlot;
  recipes: T.RecipeSummary[];
  suggested: T.RecipeSummary[];
  sources: T.LeftoverSource[];
  batches: T.FrozenBatch[];
}

const toSummaries = (suggestions: T.RecipeSuggestion[], recipes: T.RecipeSummary[]) =>
  suggestions.map(s => recipes.find(r => r.publicId === s.recipePublicId)).filter((r): r is T.RecipeSummary => r !== undefined);

/** How long before the meal a task for it is due by default. */
const MINUTES_BEFORE_MEAL = 30;

/** "19:30": the meal time minus {@link MINUTES_BEFORE_MEAL}. */
function beforeMeal(mealTime: T.TimeOnly | undefined): string {
  const [h, m] = (mealTime ?? '20:00').split(':').map(Number);
  const total = Math.max(0, h * 60 + m - MINUTES_BEFORE_MEAL);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** A one-off task for the meal, in a dialog so what is being planned is not lost. */
function MealTaskDialog({ slot, initialTitle, onClose }: {
  slot: T.MealSlot; initialTitle: string; onClose: (created: boolean) => void;
}) {
  const { household, members } = useHousehold();
  const [title, setTitle] = useState(initialTitle);
  const [date, setDate] = useState<T.DateOnly>(slot.date.substring(0, 10));
  const [dueTime, setDueTime] = useState(beforeMeal(slot.mealType === 'Lunch' ? household?.lunchTime : household?.dinnerTime));
  const [assignee, setAssignee] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) return setError('Indica a tarefa.');
    setBusy(true);
    setError(null);
    try {
      await api.createTask({
        title: title.trim(),
        category: 'Meals',
        assigneePublicId: assignee || null,
        dueTime: toTimeOnly(dueTime),
        recurrence: { type: 'None', startDate: date, endDate: null, interval: 1, days: [], dayOfMonth: null, timesPerWeek: null },
        mealSlotPublicId: slot.publicId,
      });
      onClose(true);
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Tarefa da refeição</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Tarefa" placeholder="Ex.: Ir buscar a comida" value={title} autoFocus
            onChange={e => setTitle(e.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <Stack direction="row" spacing={1}>
            <TextField
              type="date" label="Dia" value={date} onChange={e => setDate(e.target.value)} sx={{ flex: 1 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="time" label="Hora" value={dueTime} onChange={e => setDueTime(e.target.value)} sx={{ flex: 1 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          <TextField select label="Quem faz" value={assignee} onChange={e => setAssignee(e.target.value)}>
            <MenuItem value="">Ninguém em particular</MenuItem>
            {members.map(m => <MenuItem key={m.publicId} value={m.publicId}>{m.name}</MenuItem>)}
          </TextField>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy}>Criar tarefa</Button>
      </DialogActions>
    </Dialog>
  );
}

async function load(slotId: T.Guid, week: T.DateOnly): Promise<Loaded> {
  const plan = await api.getWeekPlan(week);
  const slot = plan.slots.find(s => s.publicId === slotId);
  if (!slot) throw new Error('Esta refeição não existe nesta semana.');
  const [sources, recipes, suggestions, freezer] = await Promise.all([
    api.getLeftoverSources(slotId), api.getRecipes(), api.getSlotSuggestions(slotId, false), api.getFreezer(true),
  ]);
  // What is left, plus what this meal already eats
  const batches = freezer
    .filter(b => (!b.isFinished && b.remaining > 0) || b.publicId === slot.frozenBatchPublicId)
    .filter(b => b.frozenOn.substring(0, 10) <= slot.date.substring(0, 10));
  return { slot, recipes, suggested: toSummaries(suggestions, recipes), sources, batches };
}

function SlotEditor({ data, week }: { data: Loaded; week: T.DateOnly }) {
  const navigate = useNavigate();
  const { members } = useHousehold();
  const { slot, recipes, sources, batches } = data;

  const [kind, setKind] = useState<T.MealSlotKind>(slot.kind);
  const [recipe, setRecipe] = useState<T.RecipeSummary | null>(recipes.find(r => r.publicId === slot.recipePublicId) ?? null);
  const [suggested, setSuggested] = useState(data.suggested);
  const [quickOnly, setQuickOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [sides, setSides] = useState<T.RecipeSide[]>(slot.sides);
  const [batchId, setBatchId] = useState<T.Guid>(batches.some(b => b.publicId === slot.frozenBatchPublicId) ? slot.frozenBatchPublicId! : '');
  const [sourceId, setSourceId] = useState<string>(() => {
    if (sources.some(s => s.slotPublicId === slot.leftoversFromSlotPublicId)) return slot.leftoversFromSlotPublicId!;
    return slot.kind === 'Leftovers' || sources.length === 0 ? NO_SOURCE : sources[0].slotPublicId;
  });
  const [text, setText] = useState(slot.kind === 'FreeText' || slot.kind === 'Out' ? slot.freeText ?? '' : '');
  const [attendees, setAttendees] = useState<T.Guid[]>(slot.attendeePublicIds);
  const [guests, setGuests] = useState(slot.guests);
  const [dishes, setDishes] = useState<T.MealDish[]>(slot.dishes);
  const [dishCourse, setDishCourse] = useState<T.MealCourse | null>(null);
  const [servingsText, setServingsText] = useState(decimalText(slot.servingsOverride));
  const [freezeText, setFreezeText] = useState(decimalText(slot.freezeServings));
  const [locked, setLocked] = useState(slot.isLocked);
  const [chooser, setChooser] = useState(false);
  const [tasks, setTasks] = useState<T.MealSlotTask[]>(slot.tasks ?? []);
  const [taskDialog, setTaskDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const batch = batches.find(b => b.publicId === batchId) ?? null;
  /** The dish whose sides are offered: the chosen recipe, or the recipe of the chosen frozen batch. */
  const dish = kind === 'Recipe' && recipe ? { id: recipe.publicId, usualSides: recipe.usualSides }
    : kind === 'Frozen' && batch ? { id: batch.recipePublicId, usualSides: batch.usualSides }
    : null;

  /** Another dish: start with its suggested side (the usual one made longest ago). */
  const startSides = async (recipeId: T.Guid, usualSides: T.RecipeSide[]) => {
    setSides([]);
    if (usualSides.length === 0) return;
    try {
      const side = await api.suggestSide(recipeId);
      if (side) setSides([side]);
    } catch {
      // Without the suggestion the sides just start empty
    }
  };

  const pickRecipe = (r: T.RecipeSummary) => {
    setRecipe(r);
    setSearch('');
    startSides(r.publicId, r.usualSides);
  };

  const pickBatch = (id: T.Guid) => {
    setBatchId(id);
    const b = batches.find(x => x.publicId === id);
    if (b) startSides(b.recipePublicId, b.usualSides);
  };

  const toggleQuick = async (value: boolean) => {
    setQuickOnly(value);
    try {
      setSuggested(toSummaries(await api.getSlotSuggestions(slot.publicId, value), recipes));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleSide = (side: T.RecipeSide) =>
    setSides(list => (list.some(s => s.publicId === side.publicId) ? list.filter(s => s.publicId !== side.publicId) : list.concat(side)));

  const toggleAttendee = (id: T.Guid) =>
    setAttendees(list => toggleIn(list, id));

  const key = searchKey(search);
  const results = (key.length > 0 ? recipes.filter(r => searchKey(r.name).includes(key)) : suggested)
    .filter(r => r.publicId !== recipe?.publicId)
    .slice(0, MAX_RECIPE_RESULTS);

  const ownServings = members.filter(m => attendees.includes(m.publicId)).reduce((sum, m) => sum + m.portionWeight, 0) + guests;
  const servingsHint = slot.leftoverConsumerCount > 0
    ? `Automático: ${servings(ownServings)} + as sobras para depois.`
    : `Automático: ${servings(ownServings)}.`;

  const more: string[] = [];
  const servingsValue = parseNumber(servingsText);
  const freezeValue = parseNumber(freezeText);
  if (servingsValue !== null) more.push(`${number(servingsValue, 2)} doses`);
  if (kind === 'Recipe' && freezeValue !== null) more.push(`congelar ${number(freezeValue, 2)}`);
  if (locked) more.push('bloqueada');

  const back = () => navigate(planUrl(week));

  /** Named after what is being planned, even if not saved yet. */
  const suggestedTaskTitle = kind === 'Out' ? (text.trim() ? `Ir buscar a comida (${text.trim()})` : 'Ir buscar a comida')
    : kind === 'Leftovers' ? 'Completar as sobras'
    : '';

  const closeTaskDialog = async (created: boolean) => {
    setTaskDialog(false);
    if (!created) return;
    try {
      const plan = await api.getWeekPlan(week);
      setTasks(plan.slots.find(s => s.publicId === slot.publicId)?.tasks ?? []);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const save = async () => {
    setError(null);
    if (kind === 'Recipe' && !recipe) return setError('Escolhe a receita.');
    if (kind === 'Frozen' && !batch) return setError('Escolhe o que vais descongelar.');
    let freeze = 0;
    if (kind === 'Recipe' && freezeText.trim()) {
      if (freezeValue === null || freezeValue < 0) return setError('As doses a congelar devem ser um número, ou fica em branco.');
      freeze = freezeValue;
    }
    if (kind === 'FreeText' && !text.trim()) return setError('Escreve o que se vai comer.');
    let servingsOverride: number | null = null;
    if (servingsText.trim()) {
      if (servingsValue === null || servingsValue <= 0) return setError('As doses devem ser um número positivo, ou fica em branco para automático.');
      servingsOverride = servingsValue;
    }

    const request: T.SaveMealSlotRequest = {
      kind,
      recipePublicId: kind === 'Recipe' ? recipe!.publicId : null,
      leftoversFromSlotPublicId: kind === 'Leftovers' && sourceId !== NO_SOURCE ? sourceId : null,
      freeText: kind === 'FreeText' || kind === 'Out' ? text.trim() : null,
      servingsOverride,
      isLocked: locked,
      attendeePublicIds: members.filter(m => attendees.includes(m.publicId)).map(m => m.publicId),
      sidePublicIds: kind === 'Recipe' || kind === 'Frozen' ? sides.map(s => s.publicId) : null,
      frozenBatchPublicId: kind === 'Frozen' ? batch!.publicId : null,
      freezeServings: kind === 'Recipe' ? freeze : null,
      dishes: kind === 'Out' ? [] : dishes.map(d => ({
        course: d.course, recipePublicId: d.recipePublicId ?? null, freeText: d.recipePublicId ? null : d.freeText ?? d.name, servings: d.servingsOverride ?? null,
      })),
      guests,
    };

    setBusy(true);
    try {
      await api.updateMealSlot(slot.publicId, request);
      back();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
      setBusy(false);
    }
  };

  const shownSides = dish
    ? dish.usualSides.concat(sides.filter(s => dish.usualSides.every(u => u.publicId !== s.publicId)))
    : [];

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton aria-label="Voltar" onClick={back} edge="start"><ArrowBackIcon /></IconButton>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>{dayLabel(slot.date)} · {mealText(slot.mealType)}</Typography>
      </Stack>
      {slot.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1, whiteSpace: 'pre-line' }}>{slot.warnings.map(mealWarning).join('\n')}</Alert>
      )}

      <SectionTitle>O que se come</SectionTitle>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {MEAL_KINDS.map(k => (
          <Chip
            key={k.value}
            label={k.text}
            color={kind === k.value ? 'primary' : 'default'}
            variant={kind === k.value ? 'filled' : 'outlined'}
            onClick={() => setKind(k.value)}
          />
        ))}
      </Box>

      {kind === 'Recipe' && (
        <Box sx={{ mt: 2 }}>
          {recipe && (
            <Card variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{recipe.name}</Typography>
              <Typography variant="caption" color="text.secondary">{recipeSubtitle(recipe)}</Typography>
            </Card>
          )}
          <TextField
            fullWidth size="small" placeholder="Procurar receita" value={search} onChange={e => setSearch(e.target.value)}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">{key.length > 0 ? 'Resultados' : 'Sugestões para esta refeição'}</Typography>
            <FormControlLabel
              control={<Switch size="small" checked={quickOnly} onChange={e => toggleQuick(e.target.checked)} />}
              label={<Typography variant="caption">Só rápidas</Typography>}
              labelPlacement="start"
            />
          </Stack>
          {results.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {key.length > 0 ? 'Nenhuma receita com esse nome.' : 'Sem sugestões para esta refeição.'}
            </Typography>
          ) : (
            <Paper variant="outlined">
              <List dense disablePadding>
                {results.map(r => (
                  <ListItemButton key={r.publicId} onClick={() => pickRecipe(r)}>
                    <ListItemText primary={r.name} secondary={recipeSubtitle(r)} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      )}

      {kind === 'Frozen' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
            {batches.length === 0
              ? 'O congelador está vazio. Para congelar, numa refeição com receita indica as doses a mais para congelar.'
              : 'O que vais descongelar (a tarefa de descongelar aparece na véspera).'}
          </Typography>
          {batches.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel id="frozen-label">Escolhe do congelador</InputLabel>
              <Select labelId="frozen-label" label="Escolhe do congelador" value={batchId} onChange={e => pickBatch(e.target.value)}>
                {batches.map(b => <MenuItem key={b.publicId} value={b.publicId}>{frozenPickerText(b)}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      {dish && (
        <>
          <SectionTitle>Acompanhamentos</SectionTitle>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {shownSides.map(side => {
              const on = sides.some(s => s.publicId === side.publicId);
              return (
                <Chip
                  key={side.publicId}
                  label={side.name}
                  color="primary"
                  variant={on ? 'filled' : 'outlined'}
                  aria-pressed={on}
                  onClick={() => toggleSide(side)}
                />
              );
            })}
            <Chip label="+ Outro" variant="outlined" onClick={() => setChooser(true)} sx={{ borderStyle: 'dashed' }} />
          </Box>
          {chooser && (
            <SideChooserDialog
              open
              current={sides}
              options={recipes.filter(r => r.course === 'Side' && r.publicId !== dish.id).map(asSide)}
              onClose={chosen => { setChooser(false); if (chosen) setSides(chosen); }}
            />
          )}
        </>
      )}

      {kind === 'Leftovers' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>De que refeição são as sobras? (últimos 3 dias)</Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="source-label">Escolhe a refeição</InputLabel>
            <Select labelId="source-label" label="Escolhe a refeição" value={sourceId} onChange={e => setSourceId(e.target.value)}>
              {sources.map(s => <MenuItem key={s.slotPublicId} value={s.slotPublicId}>{leftoverSourceText(s)}</MenuItem>)}
              <MenuItem value={NO_SOURCE}>Ainda não sei</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {(kind === 'FreeText' || kind === 'Out') && (
        <TextField
          fullWidth size="small" sx={{ mt: 2 }} value={text} onChange={e => setText(e.target.value)}
          placeholder={kind === 'Out' ? 'Onde? (opcional)' : 'Ex.: Pizza, Cantina'}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
      )}

      {kind !== 'Out' && (
        <>
          <SectionTitle>Entradas, sobremesas e outros pratos</SectionTitle>
          {dishes.length > 0 && (
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              {dishes.map((d, i) => (
                <Chip
                  key={`${d.course}-${d.recipePublicId ?? d.name}-${i}`}
                  sx={{ justifyContent: 'space-between', alignSelf: 'flex-start', maxWidth: '100%' }}
                  label={`${mealCourseText(d.course)}: ${d.name}${d.servingsOverride ? ` · ${servings(d.servingsOverride)}` : ''}`}
                  onDelete={() => setDishes(list => list.filter((_, j) => j !== i))}
                />
              ))}
            </Stack>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {MEAL_COURSES.map(c => (
              <Chip
                key={c.value}
                label={`+ ${c.add}`}
                variant="outlined"
                disabled={dishes.length >= MAX_DISHES}
                onClick={() => setDishCourse(c.value)}
                sx={{ borderStyle: 'dashed' }}
              />
            ))}
          </Box>
          {dishCourse && (
            <DishDialog
              course={dishCourse}
              recipes={recipes}
              onClose={dish => {
                setDishCourse(null);
                // Starters first, then other main dishes, then desserts
                if (dish) setDishes(list => list.concat(dish).sort((a, b) =>
                  MEAL_COURSES.findIndex(c => c.value === a.course) - MEAL_COURSES.findIndex(c => c.value === b.course)));
              }}
            />
          )}
        </>
      )}

      <SectionTitle>Quem come</SectionTitle>
      <MemberChips members={members} selected={attendees} onToggle={toggleAttendee} />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          Convidados{guests > 0 ? ` · ${guestsText(guests)}` : ''}
        </Typography>
        <IconButton aria-label="Menos um convidado" size="small" onClick={() => setGuests(g => Math.max(0, g - 1))} disabled={guests === 0}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Typography variant="body1" sx={{ minWidth: 24, textAlign: 'center' }} aria-live="polite">{guests}</Typography>
        <IconButton aria-label="Mais um convidado" size="small" onClick={() => setGuests(g => Math.min(MAX_GUESTS, g + 1))} disabled={guests >= MAX_GUESTS}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>

      <SectionTitle>Tarefas</SectionTitle>
      <Typography variant="caption" color="text.secondary" component="p">
        {kind === 'Out' ? 'Ex.: ir buscar a comida.'
          : kind === 'Leftovers' ? 'Ex.: fazer arroz para completar as sobras.'
          : 'Algo a fazer para esta refeição, além dos passos da receita.'}
      </Typography>
      {tasks.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 1 }}>
          <List dense disablePadding>
            {tasks.map(t => {
              const details = [
                t.date.substring(0, 10) !== slot.date.substring(0, 10) ? dayLabel(t.date) : '',
                members.find(m => m.publicId === t.assigneePublicId)?.name.split(' ')[0] ?? '',
              ].filter(Boolean).join(' · ');
              return (
                <ListItemButton key={t.taskPublicId} onClick={() => navigate(`/casa/tarefas/${t.taskPublicId}`)}>
                  <ListItemText
                    primary={mealTaskText(t)} secondary={details || undefined}
                    slotProps={{ primary: { color: t.status === 'Pending' ? undefined : 'text.secondary' } }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      )}
      <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => setTaskDialog(true)}>+ Tarefa</Button>
      {taskDialog && <MealTaskDialog slot={slot} initialTitle={suggestedTaskTitle} onClose={closeTaskDialog} />}

      <Accordion variant="outlined" defaultExpanded={slot.freezeServings !== null && slot.freezeServings !== undefined} sx={{ mt: 2 }} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle2">Mais opções</Typography>
            <Typography variant="caption" color="text.secondary">{more.length === 0 ? 'Doses, congelar, bloquear' : more.join(' · ')}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="Doses" size="small" value={servingsText} onChange={e => setServingsText(e.target.value)} placeholder="Automático"
              helperText={servingsHint} slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            {kind === 'Recipe' && (
              <TextField
                label="Doses a mais para congelar" size="small" value={freezeText} onChange={e => setFreezeText(e.target.value)}
                placeholder="Ex.: 4 — ficam no congelador" slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
            )}
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="subtitle2">Bloquear</Typography>
                <Typography variant="caption" color="text.secondary">A sugestão automática da semana não mexe nesta refeição.</Typography>
              </Box>
              <Switch checked={locked} onChange={e => setLocked(e.target.checked)} slotProps={{ input: { 'aria-label': 'Bloquear' } }} />
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {error && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}

      {/* Always in reach */}
      <Box sx={{ position: 'sticky', bottom: { xs: 64, md: 0 }, py: 1.5, mt: 2, bgcolor: 'background.default', zIndex: 1 }}>
        <Button fullWidth variant="contained" size="large" onClick={save} disabled={busy} startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}>
          Guardar
        </Button>
      </Box>
    </Box>
  );
}

function SlotEditLoader() {
  const { slotId = '' } = useParams();
  const week = useWeekParam();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useLoad(() => load(slotId, week), [slotId, week]);

  if (error) {
    return (
      <EmptyState
        title="Não foi possível abrir a refeição"
        text={error}
        action={<Stack direction="row" spacing={1} justifyContent="center">
          <Button onClick={() => navigate(planUrl(week))}>Voltar ao plano</Button>
          <Button variant="contained" onClick={reload}>Tentar de novo</Button>
        </Stack>}
      />
    );
  }
  // Never the editor of the meal left behind while the next one loads
  if (loading || !data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  return <SlotEditor key={data.slot.publicId} data={data} week={week} />;
}

/** Edits one meal of the plan. */
const MealSlotEditPage: React.FC = () => <NeedsHousehold><SlotEditLoader /></NeedsHousehold>;

export default MealSlotEditPage;
